import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface AllowedEmailDomain {
  id: string;
  organization_id: string;
  domain: string;
  created_by: string | null;
  created_at: string;
}

export const useAllowedEmailDomains = () => {
  const { organizationId, user } = useAuth();
  const queryClient = useQueryClient();

  const { data: domains = [], isLoading } = useQuery({
    queryKey: ['allowed-email-domains', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      const { data, error } = await supabase
        .from('allowed_email_domains')
        .select('*')
        .eq('organization_id', organizationId)
        .order('domain', { ascending: true });
      if (error) throw error;
      return data as AllowedEmailDomain[];
    },
    enabled: !!organizationId,
  });

  const addDomain = useMutation({
    mutationFn: async (rawDomain: string) => {
      if (!organizationId) throw new Error('No organization');
      const cleaned = rawDomain.trim().toLowerCase().replace(/^@+/, '');
      if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(cleaned)) {
        throw new Error('Enter a valid domain like company.com');
      }
      const { data, error } = await supabase
        .from('allowed_email_domains')
        .insert({
          organization_id: organizationId,
          domain: cleaned,
          created_by: user?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowed-email-domains', organizationId] });
      toast.success('Domain added');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to add domain'),
  });

  const removeDomain = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('allowed_email_domains').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowed-email-domains', organizationId] });
      toast.success('Domain removed');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to remove domain'),
  });

  return {
    domains,
    isLoading,
    addDomain: addDomain.mutate,
    removeDomain: removeDomain.mutate,
    isAdding: addDomain.isPending,
  };
};
